/**
 * Die Karte der Glocke: drei Antworten. (07.10.2026)
 *
 * Martin, 07.10.: *„Das ist unbedienbar … ich weiß ehrlich gesagt nicht, was ich
 * mit der Liste anfangen soll.“* Und seine Regel vom 05.10.: *„Viele Details
 * brauche ich nicht zu sehen.“* Die Karte beantwortet darum nur noch:
 *
 *   ① Braucht dich   höchstens drei Zeilen, je Zeile EIN Knopf („Okay“,
 *                    „Ansehen“, „Benennen“); der Rest hinter „+ 2 weitere“
 *   ② Fertig         EINE Zeile „7 Dinge fertig“, aufklappbar; darunter höchstens
 *                    fünf Zeilen, die älteren hinter „Frühere anzeigen“
 *   ③ Routine        EINE Zeile „Routine · 7 Berichte“, eingeklappt
 *
 * Dieses Modul nimmt, was der Server geordnet hat (shared/glocke-ordnung.ts), und
 * zeichnet es. Es zählt nichts, bündelt nichts und urteilt über nichts — so gibt es
 * keine zweite Wahrheit neben dem Server. Was die Knöpfe tun, entscheidet der Aufrufer
 * (frontend/lib/glocke.ts): hier hängen nur die Rückrufe dran.
 *
 * Die Zustände „aufgeklappt“ liegen in einer Menge, die der Aufrufer behält — nach
 * jedem Abhaken wird die Karte neu gezeichnet, und was Martin geöffnet hat, bleibt offen.
 */
import { relativeZeitInWorten, uhrzeitInWorten } from "./relative-time.js";
import { icon } from "./icons.js";
import type { GlockenHandlung, GlockenKarteZeile, GlockenOrdnung, GlockenSerie } from "../../shared/glocke-ordnung";
import { kuerzeFuerAnzeige, ohneLaufMarker } from "../../shared/html-text";

/** Höchstens so viele Zeilen „Braucht dich“ stehen offen da; der Rest hinter „+ N weitere“. */
const BRAUCHT_SICHTBAR = 3;
/** Höchstens so viele Zeilen „Fertig“ stehen unter der aufgeklappten Zeile; die älteren hinter „Frühere anzeigen“. */
const FERTIG_SICHTBAR = 5;

/** Die Antwort von GET /api/glocke in der einfachen Form. */
export type KartenAntwort = GlockenOrdnung & { ok?: boolean; einfach?: boolean; ungesehen?: number };

export interface KartenAktionen {
  /** Der Knopf einer Zeile in „Braucht dich“ („Okay“, „Ansehen“, „Benennen“). */
  handle: (zeile: GlockenKarteZeile, knopf: HTMLElement) => void;
  /** „Alles als gelesen markieren“ — mit allen ungesehenen ids, die die Karte zeigt. */
  alles: (offen: number[], knopf: HTMLElement) => void;
}

type El<K extends keyof HTMLElementTagNameMap> = HTMLElementTagNameMap[K];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse?: string, text?: string): El<K> {
  const e = document.createElement(tag);
  if (klasse) e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** Ein Schalter, der eine Liste auf- und zuklappt; der Zustand überlebt das Neuzeichnen. */
function klappSchalter(knopf: HTMLButtonElement, ziel: HTMLElement, id: string, auf: Set<string>): void {
  ziel.id = id;
  knopf.type = "button";
  knopf.setAttribute("aria-controls", id);
  const setze = (offen: boolean): void => {
    knopf.setAttribute("aria-expanded", String(offen));
    ziel.hidden = !offen;
  };
  setze(auf.has(id));
  knopf.appendChild(icon("chevron-down", { size: 16, stroke: 1.6, className: "glocke-pfeil" }));
  knopf.addEventListener("click", () => {
    const offen = knopf.getAttribute("aria-expanded") !== "true";
    if (offen) auf.add(id);
    else auf.delete(id);
    setze(offen);
  });
}

/** Die kleine Zeile darunter: die Zeit — bei einem Bündel „zuletzt vor …“. */
function zeitText(z: Pick<GlockenKarteZeile, "anzahl" | "createdAt">): string {
  const wann = relativeZeitInWorten(z.createdAt);
  return z.anzahl > 1 ? `zuletzt ${wann}` : wann;
}

function saetzchen(klasse: string, text: string): HTMLSpanElement {
  return el("span", klasse, text);
}

/**
 * Eine Zeile: Titel, EIN Satz, Zeit — bei einem Bündel zusätzlich „Einzelheiten“. `knopf` ist der
 * Knopf rechts (nur in „Braucht dich“).
 */
function baueZeile(z: GlockenKarteZeile, auf: Set<string>, knopf?: HTMLElement): HTMLLIElement {
  const li = el("li", "glocke-posten" + (z.neu ? " glocke-posten--neu" : ""));
  const punkt = el("span", "glocke-punkt" + (z.neu ? "" : " glocke-punkt--leer"));
  punkt.setAttribute("aria-hidden", "true");
  const text = el("span", "glocke-text");
  text.appendChild(el("strong", "glocke-titel", z.titel));
  if (z.text) text.appendChild(saetzchen("glocke-zusammenfassung", z.text));
  // Die Zeit und — bei einem Bündel — „Einzelheiten“ teilen sich EINE Zeile: ein Bündel soll Platz sparen, nicht kosten.
  const metaZeile = el("span", "glocke-meta-zeile");
  metaZeile.appendChild(saetzchen("glocke-meta", zeitText(z)));
  text.appendChild(metaZeile);

  if (z.einzelne.length > 0) {
    const id = `glocke-einzeln-${z.ids[0] ?? 0}`;
    const schalter = el("button", "glocke-mehr glocke-mehr--einzeln");
    schalter.appendChild(saetzchen("glocke-mehr-wort", "Einzelheiten"));
    const liste = el("ul", "glocke-einzelne");
    for (const x of z.einzelne) {
      const zeile = el("li", "glocke-einzel");
      if (x.text) zeile.appendChild(saetzchen("glocke-zusammenfassung", x.text));
      // Genau, nicht „gestern“: bei gleichen Sätzen ist die Uhrzeit das Einzige, was die Einzelheiten unterscheidet.
      zeile.appendChild(saetzchen("glocke-meta", uhrzeitInWorten(x.createdAt)));
      liste.appendChild(zeile);
    }
    klappSchalter(schalter, liste, id, auf);
    metaZeile.appendChild(schalter);
    text.appendChild(liste);
  }
  li.append(punkt, text);
  if (knopf) li.appendChild(knopf);
  return li;
}

function liste(zeilen: HTMLLIElement[], klasse = "glocke-liste"): HTMLUListElement {
  const ul = el("ul", klasse);
  ul.append(...zeilen);
  return ul;
}

function gruppe(titelText: string, id: string): { sektion: HTMLElement; h: HTMLHeadingElement } {
  const sektion = el("section", "glocke-gruppe");
  sektion.setAttribute("aria-labelledby", id);
  const h = el("h3", "glocke-gruppe-titel", titelText);
  h.id = id;
  // Der Aufrufer setzt den Fokus hierher, wenn die Zeile, auf der er stand, verschwindet.
  h.tabIndex = -1;
  sektion.appendChild(h);
  return { sektion, h };
}

/** Ein einzelner Knopf (oder Link) für eine Handlung der Zeile. */
function einKnopf(z: GlockenKarteZeile, h: GlockenHandlung, a: KartenAktionen): HTMLElement {
  const k: HTMLElement = h.href ? el("a", "glocke-handlung") : el("button", "glocke-handlung");
  k.textContent = h.wort;
  if (k instanceof HTMLAnchorElement) k.href = h.href ?? "";
  if (k instanceof HTMLButtonElement) k.type = "button";
  // Ein Name, der sagt, WOFÜR: „Okay“ allein hört ein Vorleser in jeder Zeile gleich.
  k.setAttribute("aria-label", `${h.wort}: ${z.titel}`);
  // Der Ausweg „Okay“ einer Zeile mit „Benennen“ hakt ab wie jedes andere „Okay“: dieselbe Zeile, nur mit dieser Handlung.
  k.addEventListener("click", () => a.handle(z.handlung === h ? z : { ...z, handlung: h }, k));
  return k;
}

/**
 * Der Knopf rechts in einer Zeile „Braucht dich“: ein Link, wenn es eine Seite ist, sonst ein Knopf. Hakt die Handlung
 * nicht ab („Benennen“), steht „Okay“ darunter als Ausweg — nie ein Hinweis, der den Punkt hält und keinen Weg hinaus hat.
 */
function handlungsKnopf(z: GlockenKarteZeile, a: KartenAktionen): HTMLElement | undefined {
  const h = z.handlung;
  if (!h) return undefined;
  const k = einKnopf(z, h, a);
  if (!z.ausweg) return k;
  const ausweg = einKnopf(z, z.ausweg, a);
  ausweg.classList.add("glocke-handlung--ausweg");
  const paar = el("div", "glocke-handlungen");
  paar.append(k, ausweg);
  return paar;
}

function weitereKnopf(wort: string): HTMLButtonElement {
  const k = el("button", "glocke-mehr");
  k.appendChild(saetzchen("glocke-mehr-wort", wort));
  return k;
}

/** „Früher“ in einer eigenen Liste hinter einem Schalter: Zeilen ohne Handlung, Punkt als Ring. */
function frueherBlock(zeilen: GlockenKarteZeile[], id: string, auf: Set<string>): HTMLElement[] {
  if (zeilen.length === 0) return [];
  const knopf = weitereKnopf(`Frühere anzeigen (${zeilen.length})`);
  const ziel = liste(zeilen.map((z) => baueZeile(z, auf)));
  klappSchalter(knopf, ziel, id, auf);
  return [knopf, ziel];
}

/** ① „Braucht dich“. */
function baueBraucht(d: KartenAntwort, a: KartenAktionen, auf: Set<string>): HTMLElement {
  const { sektion } = gruppe("Braucht dich", "glocke-gruppe-braucht");
  if (d.braucht.length === 0) {
    sektion.appendChild(el("p", "glocke-leer", d.zahl.fertig > 0 ? "Nichts braucht dich." : "Nichts Neues."));
  } else {
    const zeile = (z: GlockenKarteZeile): HTMLLIElement => baueZeile(z, auf, handlungsKnopf(z, a));
    sektion.appendChild(liste(d.braucht.slice(0, BRAUCHT_SICHTBAR).map(zeile)));
    const rest = d.braucht.slice(BRAUCHT_SICHTBAR);
    if (rest.length > 0) {
      const knopf = weitereKnopf(`+ ${rest.length} weitere`);
      const ziel = liste(rest.map(zeile));
      klappSchalter(knopf, ziel, "glocke-weitere", auf);
      sektion.append(knopf, ziel);
    }
  }
  sektion.append(...frueherBlock(d.brauchtFrueher, "glocke-frueher-braucht", auf));
  return sektion;
}

/** Die Wörter der aufklappbaren Zeile: „7 Dinge fertig“, „ein Ding fertig“, sonst „Früher fertig“. */
function fertigWort(neu: number): string {
  if (neu === 0) return "Früher fertig";
  return neu === 1 ? "Ein Ding fertig" : `${neu} Dinge fertig`;
}

/** ② „Fertig“: eine Zeile zum Aufklappen. */
function baueFertig(d: KartenAntwort, auf: Set<string>): HTMLElement | null {
  if (d.fertig.length === 0) return null;
  const sektion = el("section", "glocke-gruppe glocke-gruppe--fertig");
  const h = el("h3", "glocke-gruppe-titel");
  const knopf = el("button", "glocke-schalter");
  knopf.appendChild(saetzchen("glocke-schalter-wort", fertigWort(d.zahl.fertig)));
  const inhalt = el("div", "glocke-fertig-inhalt");
  inhalt.appendChild(liste(d.fertig.slice(0, FERTIG_SICHTBAR).map((z) => baueZeile(z, auf))));
  inhalt.append(...frueherBlock(d.fertig.slice(FERTIG_SICHTBAR), "glocke-frueher-fertig", auf));
  klappSchalter(knopf, inhalt, "glocke-fertig", auf);
  h.appendChild(knopf);
  sektion.append(h, inhalt);
  return sektion;
}

/** Eine Serie der Routine: EINE Zeile, der neueste Lauf, „+ N frühere“. Nicht antippbar — sie zählt nie. */
function baueSerie(s: GlockenSerie): HTMLLIElement {
  const li = el("li", "glocke-serie");
  const text = el("span", "glocke-text");
  text.appendChild(el("strong", "glocke-titel", s.name));
  const satz = kuerzeFuerAnzeige(ohneLaufMarker(s.neuester.summary), 120);
  if (satz) text.appendChild(saetzchen("glocke-zusammenfassung", satz));
  const wann = relativeZeitInWorten(s.neuester.createdAt);
  text.appendChild(saetzchen("glocke-meta", s.frueher > 0 ? `${wann} · + ${s.frueher} frühere` : wann));
  li.appendChild(text);
  return li;
}

/** ③ „Routine“: unten, eingeklappt, die Überschrift ist der Schalter. */
function baueRoutine(serien: GlockenSerie[], auf: Set<string>): HTMLElement {
  const sektion = el("section", "glocke-gruppe glocke-gruppe--routine");
  const h = el("h3", "glocke-gruppe-titel");
  const knopf = el("button", "glocke-schalter");
  // Das Leerzeichen gehört in den Namen des Knopfs — sonst hört der Vorleser „Routine8 Berichte“.
  knopf.append(
    saetzchen("glocke-schalter-wort", "Routine"),
    " ",
    saetzchen("glocke-schalter-zahl", serien.length === 1 ? "ein Bericht" : `${serien.length} Berichte`),
  );
  const ziel = liste(serien.map(baueSerie));
  klappSchalter(knopf, ziel, "glocke-routine", auf);
  h.appendChild(knopf);
  sektion.append(h, ziel);
  return sektion;
}

/**
 * Die einfache Karte: ① Braucht dich, ② Fertig, ③ Routine, am Ende — klein — „Alles als gelesen
 * markieren“. Ersetzt, was im Inhalt steht.
 */
export function zeichneKarte(inhalt: HTMLElement, d: KartenAntwort, a: KartenAktionen, auf: Set<string>): void {
  inhalt.replaceChildren(baueBraucht(d, a, auf));
  const fertig = baueFertig(d, auf);
  if (fertig) inhalt.appendChild(fertig);
  if (d.routine.length > 0) inhalt.appendChild(baueRoutine(d.routine, auf));

  // Der Knopf steht nur da, wenn etwas NEU ist — wer nichts zu lesen hat, braucht keinen Knopf zum Weglegen.
  if (d.zahl.braucht + d.zahl.fertig > 0 && d.offen.length > 0) {
    const fuss = el("div", "glocke-fuss");
    const alle = el("button", "glocke-alle", "Alles als gelesen markieren");
    alle.type = "button";
    alle.addEventListener("click", () => a.alles(d.offen, alle));
    fuss.appendChild(alle);
    inhalt.appendChild(fuss);
  }
}
